import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  Signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { AuthService } from '../../core/services/auth.service';
import { CustomSyncService } from '../../core/services/custom-sync.service';
import { DatabaseService } from '../../core/services/database.service';
import { EnvironmentService } from '../../core/services/environment.service';
import { LoggerService } from '../../core/services/logger.service';
import { NetworkStateService } from '../../core/services/network-state.service';
import { OrganizationData, OrganizationService } from '../../core/services/organization.service';
import { PermissionService } from '../../core/services/permission.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserStorageService } from '../../core/services/user-storage.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { ChipComponent } from '../../shared/components/chip/chip.component';
import { CheckboxComponent } from '../../shared/components/form-controls/checkbox/checkbox.component';
import { FormFieldComponent } from '../../shared/components/form-controls/form-field/form-field.component';
import { InputComponent } from '../../shared/components/form-controls/input/input.component';
import { SelectComponent } from '../../shared/components/form-controls/select/select.component';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../shared/components/modal-dialog/modal-dialog.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    FormsModule,
    DatePipe,
    RouterLink,
    CardComponent,
    CardContentComponent,
    IconComponent,
    ButtonComponent,
    CheckboxComponent,
    ChipComponent,
    FormFieldComponent,
    InputComponent,
    SelectComponent,
    ModalDialogComponent,
    HasPermissionDirective,
    TranslatePipe,
  ],
  templateUrl: './settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './settings.component.css',
})
export class SettingsComponent implements OnInit {
  private networkState = inject(NetworkStateService);
  private customSync = inject(CustomSyncService);
  private router = inject(Router);
  private logger = inject(LoggerService).getLogger('settings');
  private auth = inject(AuthService);
  private api = inject(ApiService);
  private db = inject(DatabaseService);
  private toast = inject(ToastService);
  private translate = inject(TranslationService);
  private organizationService = inject(OrganizationService);
  private permissionService = inject(PermissionService);
  readonly environmentService = inject(EnvironmentService);
  private userStorage = inject(UserStorageService);

  readonly stayOfflineMode = this.networkState.stayOfflineMode;
  readonly isOnline = this.networkState.isOnline;
  readonly syncConnected = this.customSync.isConnected;
  stayOfflineModeValue = this.stayOfflineMode();
  readonly canUseNetwork = computed(() => this.isOnline() && !this.stayOfflineMode());
  readonly syncingManually = signal(false);
  readonly syncError = signal<string | null>(null);
  readonly syncSuccess = signal(false);

  // Camera timeout setting (in seconds)
  cameraTimeout = signal(10); // Default 10 seconds
  cameraTimeoutValue = 10; // For two-way binding with ngModel
  private readonly CAMERA_TIMEOUT_STORAGE_KEY = 'camera-auto-stop-timeout';

  // Organization management
  organizationData!: Signal<OrganizationData | null>;
  organizationName!: Signal<string | null>;
  readonly userRole = signal<string | null>(null);
  readonly showRenameDialog = signal(false);
  newOrganizationName = signal(''); // WritableSignal for two-way binding
  readonly renamingOrganization = signal(false);
  readonly organizationNameChanged = computed(() => {
    const current = this.organizationName();
    const newName = this.newOrganizationName().trim();
    return newName !== '' && newName !== current;
  });

  // Demo mode properties
  canOverrideArtwork = false;
  currentEnvironment = '';
  artworkEnvironmentOptions: { value: string; label: string }[] = [];
  selectedArtworkEnv = '';
  expiryMinutes = 15;
  overrideExpiresAt: Date | null = null;

  async ngOnInit() {
    // Load camera timeout preference
    const storedTimeout = this.userStorage.getItem(this.CAMERA_TIMEOUT_STORAGE_KEY);
    if (storedTimeout) {
      const timeoutValue = parseInt(storedTimeout, 10);
      if (!isNaN(timeoutValue) && timeoutValue >= 5 && timeoutValue <= 180) {
        this.cameraTimeout.set(timeoutValue);
        this.cameraTimeoutValue = timeoutValue;
      }
    }

    // Initialize reactive organization query
    const token = this.auth.getToken();
    if (token) {
      try {
        const payload = this.auth.decodeJwt(token);
        const currentCustomerId = payload.customer_id;

        // Create reactive query for organization data
        const query = this.organizationService.createOrganizationQuery(currentCustomerId);
        this.organizationData = query.data;
        this.organizationName = computed(() => this.organizationData()?.name || null);

        // Fetch user's organizations to get role
        const response = await fetch(`${this.api['apiUrl']}/customers/my-organizations`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const currentOrg = data.organizations?.find(
            (org: any) => org.customer_id === currentCustomerId,
          );

          if (currentOrg) {
            this.userRole.set(currentOrg.role);
          }
        }
      } catch (error) {
        this.logger.error('Failed to fetch user role:', error);
      }
    }

    // Initialize demo mode settings
    this.initializeDemoMode();
  }

  async toggleStayOfflineMode() {
    await this.networkState.toggleStayOfflineMode();
  }

  onCameraTimeoutChange(value: number) {
    if (!isNaN(value) && value >= 5 && value <= 180) {
      this.cameraTimeout.set(value);
      this.cameraTimeoutValue = value;
      // Save to user-scoped storage
      this.userStorage.setItem(this.CAMERA_TIMEOUT_STORAGE_KEY, value.toString());
    } else {
      // Show error feedback for invalid values
      this.toast.error(this.translate.translate('settings.scanner.cameraTimeout.invalidRange'));
    }
  }

  async triggerManualSync() {
    if (!this.isOnline()) {
      this.syncError.set('Cannot sync while offline');
      return;
    }

    if (!this.stayOfflineMode()) {
      this.syncError.set('Manual sync only available in stay offline mode');
      return;
    }

    this.syncingManually.set(true);
    this.syncError.set(null);
    this.syncSuccess.set(false);

    const startTime = Date.now();
    const minDuration = 1000; // 1 second minimum feedback time

    try {
      await this.networkState.manualSync();

      // Ensure minimum duration for visual feedback
      const elapsed = Date.now() - startTime;
      if (elapsed < minDuration) {
        await new Promise((resolve) => setTimeout(resolve, minDuration - elapsed));
      }

      this.syncSuccess.set(true);

      // Hide success message after 3 seconds
      setTimeout(() => {
        this.syncSuccess.set(false);
      }, 3000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sync failed';
      this.syncError.set(errorMessage);
      this.logger.error('Manual sync error:', error);
    } finally {
      this.syncingManually.set(false);
    }
  }

  dismissSyncError() {
    this.syncError.set(null);
  }

  showRenameOrganization() {
    if (!this.canUseNetwork()) {
      this.toast.error(this.translate.translate('settings.organization.renameOfflineError'));
      return;
    }

    this.newOrganizationName.set(this.organizationName() || '');
    this.showRenameDialog.set(true);
  }

  cancelRename() {
    this.showRenameDialog.set(false);
    this.newOrganizationName.set('');
  }

  async confirmRename() {
    const newName = this.newOrganizationName().trim();

    if (!newName) {
      this.toast.error(this.translate.translate('settings.organization.nameRequired'));
      return;
    }

    if (newName.length < 2) {
      this.toast.error(this.translate.translate('settings.organization.nameTooShort'));
      return;
    }

    if (newName === this.organizationName()) {
      this.cancelRename();
      return;
    }

    try {
      this.renamingOrganization.set(true);

      const token = this.auth.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const payload = this.auth.decodeJwt(token);
      const customerId = payload.customer_id;

      await firstValueFrom(this.api.updateOrganizationName(customerId, newName));

      this.toast.success(this.translate.translate('settings.organization.renameSuccess'));
      this.showRenameDialog.set(false);
      this.newOrganizationName.set('');

      // Manually trigger table refresh since sync changes don't always trigger onUpdate hooks
      // This ensures reactive queries pick up the change immediately
      this.db.notifyDataChange('organization');
    } catch (error) {
      this.logger.error('Failed to rename organization:', error);
      this.toast.error(this.translate.translate('settings.organization.renameFailed'));
    } finally {
      this.renamingOrganization.set(false);
    }
  }

  isSuperAdmin(): boolean {
    return this.permissionService.isSuperAdmin();
  }

  private initializeDemoMode(): void {
    // Check if artwork override feature is available
    // This checks both environment-based permissions AND user permissions
    this.canOverrideArtwork = this.permissionService.has('feature.artwork-override');

    if (!this.canOverrideArtwork) return;

    // Get current environment
    this.currentEnvironment = this.environmentService.getCurrentEnvironment();

    // Get available environments for dropdown
    this.artworkEnvironmentOptions = this.environmentService.getAvailableEnvironments();

    // Get current override if active
    const override = this.environmentService.getCurrentOverride();
    if (override) {
      this.selectedArtworkEnv = override.environment;
      this.overrideExpiresAt = override.expiresAt;
    } else {
      this.selectedArtworkEnv = this.currentEnvironment;
    }

    // Set default expiry duration
    this.expiryMinutes = this.environmentService.getDefaultExpiryMinutes();
  }

  onArtworkEnvChange(environmentName: string): void {
    // Update override with current expiry setting
    this.environmentService.setArtworkOverride(environmentName, this.expiryMinutes);

    // Update UI state
    const override = this.environmentService.getCurrentOverride();
    if (override) {
      this.overrideExpiresAt = override.expiresAt;
    } else {
      this.overrideExpiresAt = null;
    }

    // No page reload needed! The EnvPrefixPipe will reactively update all images.
  }
}
